import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { AuthService } from '../../core/auth.service';
import { SiteConfigService } from '../../core/site-config.service';
import { FeedItem } from './feeds.service';
import { FormUpload, FormUploadService } from '../forms/form-upload.service';

interface LiveGift {
  giftID: number;
  giftName: string;
  creditAmount: number;
  animationType: string | null;
  giftImage: string | null;
  isQuestionEligible: boolean;
}
interface LivePaymentGateway { id: number; method: string; }

interface LiveEventLine {
  id: string;
  type: 'message' | 'gift' | 'question' | 'reaction';
  title: string;
  text: string;
}

@Component({
  selector: 'app-feed-live-player',
  imports: [DecimalPipe],
  template: `
    <div #viewport class="live-player">
      <div #remoteStage class="remote-stage">
        @if (!remotePlaying()) {
          <div class="live-cover">
            <span class="live-dot"></span>
            <strong>{{ feed.live?.title || feed.caption || 'Canlı yayın' }}</strong>
            <small>{{ statusText() }}</small>
          </div>
        }
      </div>

      <div class="live-topbar">
        <span class="live-badge">{{ status() === 'ended' ? 'YAYIN BİTTİ' : 'CANLI' }}</span>
        <span>{{ viewerCount() }} izleyici</span>
      </div>

      <div class="reaction-burst" aria-hidden="true">
        @for (reaction of floatingReactions(); track reaction.id) {
          <span>{{ reaction.icon }}</span>
        }
      </div>

      <div class="live-dock">
        <div class="event-list">
          @for (event of eventLines(); track event.id) {
            <p [class]="event.type">
              <strong>{{ event.title }}</strong>
              <span>{{ event.text }}</span>
            </p>
          }
        </div>

        @if (status() !== 'ended') {
        <div class="live-actions">
          <button type="button" class="heart-btn" (click)="sendReaction('heart')">♥</button>

          @if (auth.loggedIn()) {
            <form class="chat-form" (submit)="sendChat($event)">
              <input
                type="text"
                [value]="chatText()"
                placeholder="Canlı sohbete yaz..."
                (input)="chatText.set($any($event.target).value)"
              />
              <button type="submit">Gönder</button>
            </form>

            <div class="gift-row">
              @for (gift of gifts(); track gift.giftID) {
                <button type="button" (click)="selectGift(gift)" [disabled]="sendingGift()">
                  @if (gift.giftImage) {
                    <img [src]="mediaUrl(gift.giftImage)" [alt]="gift.giftName" loading="lazy" />
                  } @else {
                    <span>✦</span>
                  }
                  <small>{{ gift.creditAmount | number: '1.0-0' }}</small>
                </button>
              }
            </div>
            @if (paymentGateways().length > 1) {
              <label class="payment-choice">Ödeme yöntemi
                <select [value]="paymentGatewayID()" (change)="paymentGatewayID.set(+$any($event.target).value)">
                  @for (gateway of paymentGateways(); track gateway.id) {
                    <option [value]="gateway.id">{{ gateway.method === 'credit' ? 'Kredi' : gateway.method === 'wallet' ? 'Wallet' : gateway.method }}</option>
                  }
                </select>
              </label>
            }

            @if (selectedGift(); as gift) {
              <form class="gift-compose" (submit)="sendQuestionGift($event, gift)">
                <strong>{{ gift.giftName }} ile soru gönder</strong>
                <textarea
                  [value]="giftQuestion()"
                  placeholder="Sorunu yaz..."
                  maxlength="2000"
                  (input)="giftQuestion.set($any($event.target).value)"
                ></textarea>
                @if (giftUploads().length) {
                  <div class="gift-previews">
                    @for (upload of giftUploads(); track upload.uploadID) {
                      <button type="button" (click)="removeGiftUpload(upload.uploadID)">
                        <img [src]="upload.tempFile" [alt]="upload.fileName" /><i>×</i>
                      </button>
                    }
                  </div>
                }
                <label class="gift-upload">
                  {{ uploadingGiftImage() ? 'Yükleniyor...' : 'Fotoğraf ekle' }}
                  <input type="file" accept="image/*" multiple [disabled]="uploadingGiftImage()" (change)="uploadGiftImages($event)" />
                </label>
                <div>
                  <button type="submit" [disabled]="sendingGift() || !giftQuestion().trim()">Gönder</button>
                  <button type="button" (click)="cancelGift()">Vazgeç</button>
                </div>
              </form>
            }
          } @else {
            <p class="login-note">Mesaj ve hediye için giriş yap. Kalp gönderebilirsin.</p>
          }
        </div>
        }
      </div>
    </div>
  `,
  styleUrl: './feed-live-player.component.scss'
})
export class FeedLivePlayerComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) feed!: FeedItem;

  protected readonly auth = inject(AuthService);
  private readonly api = inject(ApiClientService);
  private readonly site = inject(SiteConfigService);
  private readonly uploader = inject(FormUploadService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @ViewChild('viewport') private readonly viewport?: ElementRef<HTMLElement>;
  @ViewChild('remoteStage') private readonly remoteStage?: ElementRef<HTMLElement>;

  protected readonly status = signal<'idle' | 'connecting' | 'watching' | 'error' | 'ended'>('idle');
  protected readonly remotePlaying = signal(false);
  protected readonly viewerCount = signal(0);
  protected readonly gifts = signal<LiveGift[]>([]);
  protected readonly paymentGateways = signal<LivePaymentGateway[]>([]);
  protected readonly paymentGatewayID = signal(0);
  protected readonly eventLines = signal<LiveEventLine[]>([]);
  protected readonly chatText = signal('');
  protected readonly sendingGift = signal(false);
  protected readonly selectedGift = signal<LiveGift | null>(null);
  protected readonly giftQuestion = signal('');
  protected readonly giftUploads = signal<FormUpload[]>([]);
  protected readonly uploadingGiftImage = signal(false);
  protected readonly floatingReactions = signal<{ id: string; icon: string }[]>([]);

  protected readonly statusText = computed(() => {
    switch (this.status()) {
      case 'connecting':
        return 'Yayına bağlanılıyor...';
      case 'watching':
        return 'Yayın açık, görüntü bekleniyor...';
      case 'error':
        return 'Yayın bağlantısı kurulamadı.';
      case 'ended':
        return 'Canlı yayın sona erdi.';
      default:
        return 'Ekrana gelince yayın otomatik açılır.';
    }
  });

  private started = false;
  private observer?: IntersectionObserver;
  private agoraClient: any;
  private socket?: WebSocket;
  private socketJoined = false;
  private socketAuthenticated = false;
  private socketAuthAttempted = false;
  private socketAuthRetries = 0;
  private socketJwt = '';
  private readonly renderedMessageKeys = new Set<string>();
  private readonly renderedGiftKeys = new Set<string>();

  ngAfterViewInit(): void {
    this.viewerCount.set(this.feed.live?.viewerCount ?? 0);
    if (!this.isBrowser || !this.viewport?.nativeElement) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      void this.start();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.45)) {
        void this.start();
      }
    }, { threshold: [0, .25, .45, .7], rootMargin: '240px 0px' });
    this.observer.observe(this.viewport.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.socket?.close();
    if (this.agoraClient) {
      void this.agoraClient.leave().catch(() => undefined);
    }
  }

  protected async sendChat(event: Event): Promise<void> {
    event.preventDefault();
    const message = this.chatText().trim();
    if (!message || !this.socketJoined) {
      if (message) {
        this.pushEvent('message', 'Sistem', 'Sohbet bağlantısı hazırlanıyor. Birkaç saniye sonra tekrar dene.');
      }
      return;
    }
    if (!this.socketAuthenticated) {
      if (this.auth.loggedIn()) {
        await this.sendChatViaApi(message);
        return;
      }
      this.pushEvent('message', 'Sistem', 'Mesaj için giriş yapman gerekiyor.');
      return;
    }
    this.sendSocket('live.chat.send', {
      liveID: this.feed.live?.liveID,
      clientMessageID: this.eventID('msg'),
      message
    });
    this.chatText.set('');
  }

  private async sendChatViaApi(message: string): Promise<void> {
    const clientMessageID = this.eventID('msg');
    try {
      const response = await firstValueFrom(this.api.post<unknown>('/api/live/chat-message', {
        liveID: this.feed.live?.liveID,
        clientMessageID,
        message,
        messageType: 'text'
      }));
      const data = this.extractData(response);
      this.pushMessageOnce(data, message);
      this.chatText.set('');
    } catch {
      this.pushEvent('message', 'Sistem', 'Mesaj gönderilemedi. Oturumunu yenileyip tekrar dene.');
    }
  }

  protected sendReaction(reaction: 'heart' | 'laugh' | 'love' | 'fire' | 'clap' | 'wow' | 'sparkle'): void {
    if (!this.socketJoined) {
      return;
    }
    this.sendSocket('live.reaction.send', {
      liveID: this.feed.live?.liveID,
      reaction,
      intensity: 1,
      count: 1
    });
  }

  protected selectGift(gift: LiveGift): void {
    if (gift.isQuestionEligible) {
      this.selectedGift.set(gift);
      this.giftQuestion.set('');
      this.giftUploads.set([]);
      return;
    }
    void this.sendGift(gift);
  }

  protected cancelGift(): void {
    this.selectedGift.set(null);
    this.giftQuestion.set('');
    this.giftUploads.set([]);
  }

  protected async uploadGiftImages(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []).slice(0, 4 - this.giftUploads().length);
    if (!files.length) return;
    this.uploadingGiftImage.set(true);
    try {
      const uploaded: FormUpload[] = [];
      for (const file of files) uploaded.push(await this.uploader.upload('liveQuestionImages', file));
      this.giftUploads.update((items) => [...items, ...uploaded].slice(0, 4));
    } finally {
      this.uploadingGiftImage.set(false);
      input.value = '';
    }
  }

  protected removeGiftUpload(uploadID: number): void {
    this.giftUploads.update((items) => items.filter((item) => item.uploadID !== uploadID));
  }

  protected async sendQuestionGift(event: Event, gift: LiveGift): Promise<void> {
    event.preventDefault();
    await this.sendGift(gift, 'question', this.giftQuestion(), this.giftUploads().map((item) => item.uploadID));
    if (!this.sendingGift()) this.cancelGift();
  }

  protected async sendGift(gift: LiveGift, mode = 'gift', question = '', imageUploadIDs: number[] = []): Promise<void> {
    if (!this.auth.loggedIn() || this.sendingGift()) {
      return;
    }
    this.sendingGift.set(true);
    try {
      const response = await firstValueFrom(this.api.post<unknown>('/api/live/send-gift', {
        liveID: this.feed.live?.liveID,
        giftID: gift.giftID,
        mode,
        question,
        imageUploadIDs,
        paymentGatewayID: this.paymentGatewayID() || null,
        idempotencyKey: this.eventID('gift')
      }));
      const giftEvent = this.extractData(response)['giftEvent'];
      if (this.isRecord(giftEvent)) {
        this.pushGiftOnce(giftEvent);
      }
    } finally {
      this.sendingGift.set(false);
    }
  }

  protected mediaUrl(url: string | null): string {
    if (!url) {
      return '';
    }
    const sized = url.includes('[size]') ? url.replace('[size]', 's') : url;
    if (/^https?:\/\//.test(sized) || sized.startsWith('/')) {
      return sized;
    }
    return `/${sized}`;
  }

  private async start(): Promise<void> {
    if (this.started || !this.feed.live?.liveID) {
      return;
    }
    this.started = true;
    this.status.set('connecting');
    await this.site.ready();
    await Promise.all([
      this.startAgora(),
      this.connectSocket(),
      this.loadGiftCatalog(),
      this.loadSnapshot()
    ]);
  }

  private async startAgora(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.post<unknown>('/api/live/watch-token', {
        liveID: this.feed.live?.liveID
      }));
      const data = this.extractData(response);
      const appId = String(data['appId'] ?? '');
      const channelName = String(data['channelName'] ?? this.feed.live?.channelName ?? '');
      const token = String(data['token'] ?? '');
      const uid = Number(data['uid'] ?? 0);
      if (!appId || !channelName || !uid) {
        throw new Error('missing_agora_session');
      }

      const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
      this.agoraClient = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
      this.agoraClient.on('user-published', async (user: any, mediaType: 'audio' | 'video') => {
        await this.agoraClient.subscribe(user, mediaType);
        if (mediaType === 'video' && this.remoteStage?.nativeElement) {
          user.videoTrack?.play(this.remoteStage.nativeElement);
          this.remotePlaying.set(true);
        }
        if (mediaType === 'audio') {
          user.audioTrack?.play();
        }
      });
      this.agoraClient.on('user-unpublished', () => this.remotePlaying.set(false));
      await this.agoraClient.setClientRole('audience');
      await this.agoraClient.join(appId, channelName, token || null, uid);
      this.status.set('watching');
      this.viewerCount.set(Number(data['viewerCount'] ?? this.viewerCount()));
    } catch {
      this.status.set('error');
    }
  }

  private async connectSocket(): Promise<void> {
    if (!this.isBrowser || !this.feed.live?.liveID) {
      return;
    }
    if (this.auth.loggedIn()) {
      this.socketJwt = await this.ensureSocketJwt();
    }
    this.socket = new WebSocket(this.site.config().socketUrl);
    this.socket.addEventListener('message', (event) => this.handleSocketMessage(event.data));
  }

  private async stopPlayback(): Promise<void> {
    this.status.set('ended');
    this.remotePlaying.set(false);
    this.socketJoined = false;
    this.socket?.close();
    this.socket = undefined;
    try { await this.agoraClient?.leave(); } catch { /* already disconnected */ }
    this.agoraClient = null;
  }

  private handleSocketMessage(raw: string): void {
    const message = this.parseJson(raw);
    const event = String(message['event'] ?? '');
    if (event === 'connection.ready') {
      const token = this.socketJwt || this.getAccessToken();
      if (token) {
        this.socketAuthAttempted = true;
        this.sendSocket('auth.authenticate', { token: `Bearer ${token}` });
      } else {
        this.joinSocketRoom();
      }
      return;
    }
    if (event === 'auth.authenticated') {
      this.socketAuthenticated = true;
      this.socketAuthRetries = 0;
      this.joinSocketRoom();
      return;
    }
    if (event === 'live.room.joined') {
      this.socketJoined = true;
      this.pushEvent('message', 'Sistem', 'Canlı sohbet bağlantısı hazır.');
      return;
    }
    if (event === 'live.ended' || event === 'live.admin.stopped') {
      void this.stopPlayback();
      return;
    }
    if (event === 'error') {
      const data = this.extractData(message);
      this.pushEvent('message', 'Socket', String(data['message'] ?? data['code'] ?? 'Bağlantı hatası'));
      if (this.auth.loggedIn() && String(data['code'] ?? '').toLowerCase() === 'unauthorized' && this.socketAuthRetries < 1) {
        void this.retrySocketAuthentication();
      }
      return;
    }
    if (event === 'live.chat.sent' || event === 'live.chat.created') {
      const data = this.extractData(message);
      this.pushMessageOnce(data);
      return;
    }
    if (event === 'live.gift.sent') {
      const data = this.extractData(message);
      this.pushGiftOnce(this.isRecord(data['giftEvent']) ? data['giftEvent'] : data);
      return;
    }
    if (event === 'live.question.sent' || event === 'live.question.created') {
      const data = this.extractData(message);
      this.pushEvent('question', String(data['senderName'] ?? 'Soru'), String(data['questionPreview'] ?? data['question'] ?? 'Yeni soru geldi'));
      return;
    }
    if (event === 'live.reaction.sent' || event === 'live.heart.sent') {
      const data = this.extractData(message);
      const reaction = String(data['reaction'] ?? 'heart');
      const count = Math.max(1, Number(data['count'] ?? 1));
      for (let i = 0; i < Math.min(count, 8); i++) {
        this.pushReaction(reaction);
      }
    }
  }

  private joinSocketRoom(): void {
    this.sendSocket('live.room.join', { liveID: this.feed.live?.liveID });
  }

  private sendSocket(event: string, data: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({
      v: 1,
      id: this.eventID('ws'),
      event,
      data
    }));
  }

  private async loadGiftCatalog(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.post<unknown>('/api/live/gift-catalog', {}));
      const data = this.extractData(response);
      const gifts = this.toArray(data['gifts'] ?? data['items'] ?? data['catalog']).map((item) => ({
        giftID: Number(item['giftID'] ?? item['id'] ?? 0),
        giftName: String(item['giftName'] ?? item['title'] ?? ''),
        creditAmount: Number(item['creditAmount'] ?? 0),
        animationType: this.stringOrNull(item['animationType']),
        giftImage: this.stringOrNull(item['giftImage'] ?? item['imageUrl'] ?? item['mediaUrl']),
        isQuestionEligible: Boolean(item['isQuestionEligible'] ?? item['questionEligible'])
      })).filter((gift) => gift.giftID > 0);
      this.gifts.set(gifts.slice(0, 6));
      const gateways = this.toArray(data['paymentGateways']).map((item) => ({ id: Number(item['id'] ?? 0), method: String(item['method'] ?? '') })).filter((item) => item.id > 0 && item.method);
      this.paymentGateways.set(gateways);
      if (!this.paymentGatewayID() && gateways.length) this.paymentGatewayID.set(gateways[0].id);
    } catch {
      this.gifts.set([]);
    }
  }

  private async loadSnapshot(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.post<unknown>('/api/live/snapshot', {
        liveID: this.feed.live?.liveID
      }));
      const data = this.extractData(response);
      this.viewerCount.set(Number(data['viewerCount'] ?? this.viewerCount()));
      for (const item of this.toArray(data['messages']).slice(-4)) {
        this.pushMessageOnce(item);
      }
      for (const item of this.toArray(data['giftEvents']).slice(-4)) {
        this.pushGiftOnce(item);
      }
      for (const item of this.toArray(data['questions']).slice(-3)) {
        this.pushEvent('question', String(item['senderName'] ?? 'Soru'), String(item['questionPreview'] ?? item['question'] ?? 'Yeni soru'));
      }
    } catch {
      return;
    }
  }

  private pushEvent(type: LiveEventLine['type'], title: string, text: string): void {
    if (!text) {
      return;
    }
    this.eventLines.update((items) => [{
      id: this.eventID(type),
      type,
      title,
      text
    }, ...items].slice(0, 7));
  }

  private pushMessageOnce(data: Record<string, unknown>, fallback = ''): void {
    const message = String(data['message'] ?? fallback);
    if (!message) return;
    const key = String(data['messageID'] ?? data['clientMessageID'] ?? `${data['userID'] ?? ''}:${message}`);
    if (this.renderedMessageKeys.has(key)) return;
    this.renderedMessageKeys.add(key);
    if (this.renderedMessageKeys.size > 200) {
      const first = this.renderedMessageKeys.values().next().value;
      if (first) this.renderedMessageKeys.delete(first);
    }
    this.pushEvent('message', String(data['senderName'] ?? data['displayName'] ?? 'İzleyici'), message);
  }

  private pushGiftOnce(data: Record<string, unknown>): void {
    const giftName = String(data['giftName'] ?? 'Hediye');
    const key = String(data['giftEventID'] ?? `${data['senderProfileID'] ?? ''}:${data['giftID'] ?? ''}:${data['createdAt'] ?? ''}`);
    if (this.renderedGiftKeys.has(key)) return;
    this.renderedGiftKeys.add(key);
    if (this.renderedGiftKeys.size > 100) {
      const first = this.renderedGiftKeys.values().next().value;
      if (first) this.renderedGiftKeys.delete(first);
    }
    this.pushEvent('gift', String(data['senderName'] ?? 'İzleyici'), `${giftName} gönderdi`);
  }

  private pushReaction(reaction: string): void {
    const icons: Record<string, string> = {
      heart: '♥',
      laugh: '😄',
      love: '😍',
      fire: '🔥',
      clap: '👏',
      wow: '😮',
      sparkle: '✨'
    };
    const id = this.eventID('reaction');
    this.floatingReactions.update((items) => [...items, { id, icon: icons[reaction] ?? '♥' }].slice(-10));
    window.setTimeout(() => {
      this.floatingReactions.update((items) => items.filter((item) => item.id !== id));
    }, 1700);
  }

  private getAccessToken(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    try {
      return this.normalizeAccessToken(window.sessionStorage.getItem('aymudo.accessToken') || '');
    } catch {
      return this.normalizeAccessToken(this.getCookie('aymudo.accessToken'));
    }
  }

  private normalizeAccessToken(token: string): string {
    return String(token || '').replace(/^Bearer\s+/i, '').trim();
  }

  private async ensureSocketJwt(): Promise<string> {
    await this.auth.refresh();
    const accessToken = this.normalizeAccessToken(this.auth.accessToken());
    if (!accessToken) {
      return '';
    }
    try {
      const response = await firstValueFrom(this.api.post<unknown>('/api/live/socket-authenticate', {
        token: `Bearer ${accessToken}`
      }));
      const data = this.extractData(response);
      const user = this.isRecord(data['user']) ? data['user'] : {};
      return this.normalizeAccessToken(String(user['jwt'] ?? accessToken));
    } catch {
      return accessToken;
    }
  }

  private async retrySocketAuthentication(): Promise<void> {
    this.socketAuthRetries += 1;
    this.socketAuthenticated = false;
    this.socketJoined = false;
    const token = await this.ensureSocketJwt();
    if (!token) {
      return;
    }
    this.socketJwt = token;
    this.socketAuthAttempted = true;
    this.sendSocket('auth.authenticate', { token: `Bearer ${token}` });
  }

  private getCookie(name: string): string {
    if (typeof document === 'undefined') {
      return '';
    }
    const encodedName = encodeURIComponent(name);
    const item = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${encodedName}=`));
    return item ? decodeURIComponent(item.slice(encodedName.length + 1)) : '';
  }

  private eventID(prefix: string): string {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
  }

  private parseJson(raw: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw);
      return this.isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private extractData(response: unknown): Record<string, unknown> {
    const first = Array.isArray(response) ? response[0] : response;
    if (!this.isRecord(first)) {
      return {};
    }
    return this.isRecord(first['data']) ? first['data'] : first;
  }

  private toArray(value: unknown): Record<string, unknown>[] {
    return Array.isArray(value) ? value.filter(this.isRecord) : [];
  }

  private stringOrNull(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
